import { useState, useRef, useEffect } from "react";
import { useNPCChat } from "../../hooks/useNPCChat";
import { useCompany } from "../../contexts/CompanyContext";

interface NPCDef {
  id: string;
  name: string;
  title: string;
  initial: string;
  color: string;
  bgColor: string;
  greeting: string;
  tag: string;
}

const NPCS: NPCDef[] = [
  {
    id: "mentor",
    name: "이지현",
    title: "전 SM 전략이사 / 경영 멘토",
    initial: "이",
    color: "#D97706",
    bgColor: "#FEF3C7",
    tag: "멘토",
    greeting:
      "안녕하세요! 저는 이지현 컨설턴트입니다. 경영 전략, 아티스트 관리, 업계 트렌드 등 무엇이든 물어보세요. 함께 좋은 방향을 찾아봅시다 😊",
  },
  {
    id: "investor",
    name: "박서준",
    title: "K-Culture Ventures 대표",
    initial: "박",
    color: "#059669",
    bgColor: "#D1FAE5",
    tag: "투자자",
    greeting:
      "K-Culture Ventures 박서준입니다. 투자 유치나 재무 전략에 대해 이야기해봅시다. 숫자로 이야기하는 걸 좋아합니다.",
  },
  {
    id: "journalist",
    name: "김민준",
    title: "아이돌차트 편집장",
    initial: "김",
    color: "#EA580C",
    bgColor: "#FFEDD5",
    tag: "언론",
    greeting:
      "아이돌차트 김민준 편집장입니다. 귀사의 아티스트와 프로젝트에 대해 취재하고 싶습니다. 독자들이 궁금해하는 것들을 물어볼게요.",
  },
  {
    id: "rival",
    name: "최현우",
    title: "블랙스타 엔터테인먼트 대표",
    initial: "최",
    color: "#DC2626",
    bgColor: "#FEE2E2",
    tag: "경쟁사",
    greeting:
      "블랙스타 엔터 최현우입니다. 업계에서 우리 둘 다 살아남으려면 가끔 대화도 필요하죠. 뭘 원하시나요?",
  },
  {
    id: "fan",
    name: "이수현",
    title: "루나&스타즈 팬클럽 회장",
    initial: "이",
    color: "#E8375F",
    bgColor: "#FFE4E6",
    tag: "팬덤",
    greeting:
      "안녕하세요! 스타라이트 팬클럽 회장 이수현입니다. 팬들이 원하는 것, 아쉬운 것들을 소속사에 직접 전달하고 싶어요!",
  },
  {
    id: "legal",
    name: "장현주",
    title: "문화예술 전문 변호사",
    initial: "장",
    color: "#0D9488",
    bgColor: "#CCFBF1",
    tag: "법무",
    greeting:
      "장현주 변호사입니다. 계약, 저작권, 분쟁 등 법적 사안에 대해 자문해 드리겠습니다. 법적 리스크는 미리미리 확인하는 게 최선입니다.",
  },
];

interface NPCChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialNpcId?: string;
  context?: string;
}

function NPCChat({
  npc,
  context,
}: {
  npc: NPCDef;
  context?: string;
}) {
  const { companyState } = useCompany();
  const fullContext = `${context ?? ""} 현재 자본금: ${companyState?.capital?.toLocaleString() ?? "?"}만원, 명성: ${companyState?.reputation ?? "?"}점`.trim();
  const { messages, loading, sendMessage, clear } = useNPCChat(npc.id, fullContext);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    await sendMessage(text);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* NPC info bar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-black/[0.07]">
        <div
          className="w-10 h-10 flex items-center justify-center rounded-full text-white font-bold text-sm shrink-0"
          style={{ backgroundColor: npc.color }}
        >
          {npc.initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[#111111] font-semibold text-sm">{npc.name}</span>
            <span
              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white"
              style={{ backgroundColor: npc.color }}
            >
              {npc.tag}
            </span>
          </div>
          <p className="text-[#777777] text-[11px] truncate">{npc.title}</p>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00C896] inline-block"></span>
          <span className="text-[#00C896] text-[10px]">온라인</span>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-[#CCCCCC] hover:text-[#777777] transition-colors cursor-pointer text-xs"
            title="대화 초기화"
          >
            <i className="ri-refresh-line"></i>
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* Greeting */}
        <div className="flex items-start gap-2.5">
          <div
            className="w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-bold shrink-0 mt-0.5"
            style={{ backgroundColor: npc.color }}
          >
            {npc.initial}
          </div>
          <div
            className="max-w-[80%] rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-xs leading-relaxed"
            style={{ backgroundColor: npc.bgColor, color: "#222222" }}
          >
            {npc.greeting}
          </div>
        </div>

        {messages.map((msg) =>
          msg.role === "user" ? (
            <div key={msg.id} className="flex justify-end">
              <div className="max-w-[80%] bg-[#4B6CF7]/10 border border-[#4B6CF7]/15 rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-xs text-[#222222] leading-relaxed">
                {msg.text}
              </div>
            </div>
          ) : (
            <div key={msg.id} className="flex items-start gap-2.5 animate-slide-in-up">
              <div
                className="w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-bold shrink-0 mt-0.5"
                style={{ backgroundColor: npc.color }}
              >
                {npc.initial}
              </div>
              <div
                className="max-w-[80%] rounded-2xl rounded-tl-sm px-3.5 py-2.5 text-xs leading-relaxed"
                style={{ backgroundColor: npc.bgColor, color: "#222222" }}
              >
                {msg.text}
              </div>
            </div>
          )
        )}

        {loading && (
          <div className="flex items-start gap-2.5">
            <div
              className="w-7 h-7 flex items-center justify-center rounded-full text-white text-xs font-bold shrink-0"
              style={{ backgroundColor: npc.color }}
            >
              {npc.initial}
            </div>
            <div
              className="rounded-2xl rounded-tl-sm px-3.5 py-2.5"
              style={{ backgroundColor: npc.bgColor }}
            >
              <div className="flex items-center gap-1">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full inline-block animate-bounce"
                    style={{ backgroundColor: npc.color, animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 pb-4 pt-2 border-t border-black/[0.07]">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder={`${npc.name}에게 메시지...`}
            rows={2}
            maxLength={500}
            className="flex-1 bg-[#F7F7F7] rounded-xl px-3 py-2 text-xs outline-none border border-transparent focus:border-black/[0.12] resize-none placeholder-[#BBBBBB]"
            style={{ color: "#111111" }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 shrink-0"
            style={{ backgroundColor: npc.color }}
          >
            <i className="ri-send-plane-fill text-sm"></i>
          </button>
        </div>
        <p className="text-[#CCCCCC] text-[9px] mt-1.5 text-right">Enter로 전송 · Shift+Enter 줄바꿈</p>
      </div>
    </div>
  );
}

export default function NPCChatModal({ isOpen, onClose, initialNpcId = "mentor", context }: NPCChatModalProps) {
  const [selectedId, setSelectedId] = useState(initialNpcId);
  const selectedNpc = NPCS.find((n) => n.id === selectedId) ?? NPCS[0];

  useEffect(() => {
    if (initialNpcId) setSelectedId(initialNpcId);
  }, [initialNpcId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg h-[600px] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden mx-4 animate-slide-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-black/[0.07]">
          <div className="flex items-center gap-2">
            <i className="ri-robot-2-line text-[#D97706]"></i>
            <span className="text-[#111111] font-semibold text-sm">AI NPC 어드바이저</span>
            <span className="text-[9px] bg-[#D97706]/10 text-[#D97706] px-2 py-0.5 rounded-full border border-[#D97706]/20 font-medium">
              GEMINI
            </span>
          </div>
          <button type="button" onClick={onClose} className="w-7 h-7 flex items-center justify-center text-[#AAAAAA] hover:text-[#555555] cursor-pointer transition-colors">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* NPC Selector */}
        <div className="flex gap-1.5 px-4 py-2.5 border-b border-black/[0.06] overflow-x-auto">
          {NPCS.map((npc) => (
            <button
              key={npc.id}
              type="button"
              onClick={() => setSelectedId(npc.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] whitespace-nowrap cursor-pointer transition-all shrink-0 ${
                selectedId === npc.id
                  ? "font-semibold border"
                  : "text-[#888888] hover:bg-black/[0.04] border border-transparent"
              }`}
              style={
                selectedId === npc.id
                  ? {
                      backgroundColor: npc.bgColor,
                      color: npc.color,
                      borderColor: npc.color + "40",
                    }
                  : undefined
              }
            >
              <div
                className="w-5 h-5 flex items-center justify-center rounded-full text-white text-[9px] font-bold shrink-0"
                style={{ backgroundColor: selectedId === npc.id ? npc.color : "#CCCCCC" }}
              >
                {npc.initial}
              </div>
              {npc.name}
            </button>
          ))}
        </div>

        {/* Chat area (key forces re-mount on NPC switch → fresh chat per NPC) */}
        <div className="flex-1 min-h-0">
          <NPCChat key={selectedId} npc={selectedNpc} context={context} />
        </div>
      </div>
    </div>
  );
}
